import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartColors } from './chartPalette';
import { ChartTooltip } from './ChartTooltip';
import { formatNumber } from '@/utils';
import { compactIndianNumber } from './chartFormat';

export interface TrendSeries {
  dataKey: string;
  label: string;
}

interface TrendLineChartProps {
  data: Record<string, unknown>[];
  xKey: string;
  /** 1-3 series (categorical color job -- "distinct series" is the point of
   * this chart, per the dataviz skill). Assigned the fixed categorical slots
   * in order, so a series keeps its color across re-renders/filters. */
  series: TrendSeries[];
  xTickFormatter?: (value: string) => string;
  /** Value-axis ticks. Defaults to compact whole numbers ("20K", "2L") so a
   * premium series never overflows the axis gutter. */
  yTickFormatter?: (value: number) => string;
  height?: number;
  emptyMessage?: string;
}

/**
 * A trend-over-time line chart with 2 (soon 1-3) named series -- e.g. Leads
 * vs Converted per month on the Sales tab. Two series sits comfortably in
 * the skill's "1-3: color alone is comfortable, direct-label" band, so both
 * a legend (always present for >= 2 series) and end-of-line direct labels
 * are shown.
 */
export function TrendLineChart({
  data,
  xKey,
  series,
  xTickFormatter,
  yTickFormatter = compactIndianNumber,
  height = 240,
  emptyMessage = 'No data available for the selected period.',
}: TrendLineChartProps) {
  const colors = useChartColors();

  const hasValues = data.some((row) => series.some((s) => Number(row[s.dataKey]) > 0));
  if (data.length === 0 || !hasValues) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">{emptyMessage}</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
        <CartesianGrid vertical={false} stroke={colors.grid} strokeDasharray="0" />
        <XAxis
          dataKey={xKey}
          tickFormatter={xTickFormatter}
          tick={{ fill: colors.ink.muted, fontSize: 12 }}
          axisLine={{ stroke: colors.axis }}
          tickLine={false}
        />
        <YAxis
          allowDecimals={false}
          tickFormatter={(v: number) => yTickFormatter(Number(v))}
          tick={{ fill: colors.ink.muted, fontSize: 12 }}
          axisLine={{ stroke: colors.axis }}
          tickLine={false}
          width={44}
        />
        <Tooltip
          content={({ active, label, payload }) => (
            <ChartTooltip
              active={active}
              title={xTickFormatter ? xTickFormatter(String(label)) : String(label)}
              rows={(payload ?? []).map((p) => ({
                label: series.find((s) => s.dataKey === p.dataKey)?.label ?? String(p.dataKey),
                value: formatNumber(Number(p.value)),
                color: p.color,
              }))}
            />
          )}
        />
        <Legend
          wrapperStyle={{ fontSize: 12, color: colors.ink.secondary }}
          iconType="plainline"
        />
        {series.map((s, i) => (
          <Line
            key={s.dataKey}
            type="monotone"
            dataKey={s.dataKey}
            name={s.label}
            stroke={colors.categorical[i % colors.categorical.length]}
            strokeWidth={2}
            dot={{ r: 4, strokeWidth: 0, fill: colors.categorical[i % colors.categorical.length] }}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
